/**
 * HUD CHROME (B9) — the islands over the map. Spec §A.2–A.5 (positions), §E.6
 * (drive), §E.7 (the "ui" beat), §E.9 (layers card), §E.10 (reduced motion),
 * §F (copy), §H (focus, names).
 *
 *   LightIsland     HIDDEN (client direction): day is the default and only mood shown
 *   zoom island     + / − (420 settle about the centre), Back to the site, Layers
 *   map buttons     mobile: Back to the site + Layers as solo 44 px buttons (pinch zooms)
 *   layers card     every LayerFlag; hillshade waits for the DEM; Escape → focus back to Layers
 *   tools island    Share this view · Full screen (not on mobile or kiosk)
 *   attribution     full line + Powered by Flux Real; short on tight rows; opens the About sheet
 *   About sheet     the one paper surface (spec §0 decision 1)
 *
 * The UI arrives with the .ld-ui-in stagger at the intro's "ui" beat (or at
 * once when the intro is short), and in a drive everything but the zoom
 * island rests at opacity .35 and is inert. Nothing animates through React:
 * arrival, dimming and the sheet-following offsets are CSS transitions.
 *
 * Other modules open the sheet with openAbout(opener) from this file.
 */
import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type JSX, type KeyboardEvent as ReactKeyboardEvent, type MouseEvent as ReactMouseEvent } from "react";
import { createPortal } from "react-dom";
import type { HudProps } from "@/contracts/ui";
import { DUR, Icon, LAYER_ROWS, LayersCard, Logo, ZoomIsland, cx, exitMs, useReducedMotion } from "@/design";
import { actions, useApp } from "@/store/store";
import { on } from "@/store/bus";
import { AboutSheet } from "./AboutSheet";
import { openAbout } from "./aboutSignal";
import { HUD_COPY } from "./copy";
import { edgeStyle, hudGeometry, hudTier, layerRows } from "./geometry";
import { useArrival, useViewport, useZoomControl } from "./hooks";
import { Attribution, MapButtons, ToolsIsland } from "./parts";
import "./hud.css";

export { openAbout, closeAbout } from "./aboutSignal";

type CSSVars = CSSProperties & { [k: `--${string}`]: string | number | undefined };
type LayersState = "closed" | "open" | "closing";

/** §E.9: the layers card leaves in 294 leave. */
const LAYERS_OUT_MS = exitMs(DUR.calm);
/** A zoom button that turns disabled under the pointer or key hands focus to its sibling within this window. */
const ZOOM_FOCUS_MS = 1500;
/** §A.4 mobile logo: Whorl 36 at left 16, top 16 (plus the safe area). */
const MOBILE_LOGO = { left: 16, top: 16, size: 36 } as const;
/**
 * The layout's slot after the map (§H.1: map controls → map region → attribution). The attribution
 * renders there; without a layout around the HUD (harness pages) it stays in the HUD's own root.
 */
const END_SLOT = '[data-ld-slot="hud-end"]';

/** "up" while a value grows, "down" while it shrinks (the mobile sheet: 640 settle up, 448 leave down). */
function useDirection(v: number): "up" | "down" {
  const [s, setS] = useState<{ v: number; dir: "up" | "down" }>({ v, dir: "up" });
  if (s.v !== v) {
    const next = { v, dir: v > s.v ? ("up" as const) : ("down" as const) };
    setS(next);
    return next.dir;
  }
  return s.dir;
}

export function Hud({ controller, onRecenter }: HudProps): JSX.Element {
  const phase = useApp((s) => s.phase);
  const layers = useApp((s) => s.layers);
  const layout = useApp((s) => s.layout);
  const kioskFlag = useApp((s) => s.kiosk);
  const demReady = useApp((s) => s.demReady);
  const selected = useApp((s) => s.selectedId !== null);
  const sheet = useApp((s) => s.sheet);
  const reduced = useReducedMotion();
  const vp = useViewport();

  const tier = hudTier(layout, kioskFlag, vp.w);
  const kiosk = tier === "kiosk";
  const mobile = tier === "mobile";
  // (I1) In a mobile drive the sheet steps aside (B10) and the drive HUD rides above the peek inset:
  // the map buttons and attribution take the bottom corner beneath it instead of the card's half height.
  const geo = hudGeometry({ tier, width: vp.w, height: vp.h, cardOpen: selected, sheetSnap: sheet, sheet: tier === "mobile" && phase === "drive" ? 0 : undefined });
  const sheetDir = useDirection(geo.mapButtons?.bottom ?? 0);

  const { state: arrival, stagger } = useArrival(phase, reduced);
  const drive = phase === "drive";
  const zoom = useZoomControl(controller, reduced);

  /* ── layers card ─────────────────────────────────────────────────── */

  const layersId = useId();
  const controlsRef = useRef<HTMLDivElement>(null);
  const [ls, setLsState] = useState<LayersState>("closed");
  const lsRef = useRef<LayersState>("closed");
  const layersTimer = useRef(0);
  const focusIntoCard = useRef(false);

  const setLs = useCallback((s: LayersState) => {
    lsRef.current = s;
    setLsState(s);
  }, []);
  const layersButton = useCallback(() => controlsRef.current?.querySelector<HTMLButtonElement>("button[aria-controls]") ?? null, []);

  const closeLayers = useCallback(
    (focusBack: boolean) => {
      if (lsRef.current !== "open") return;
      window.clearTimeout(layersTimer.current);
      setLs("closing");
      layersTimer.current = window.setTimeout(() => setLs("closed"), reduced ? 0 : LAYERS_OUT_MS);
      if (focusBack) layersButton()?.focus({ preventScroll: true });
    },
    [reduced, setLs, layersButton],
  );
  const toggleLayers = useCallback(() => {
    if (lsRef.current === "open") {
      closeLayers(false);
      return;
    }
    window.clearTimeout(layersTimer.current);
    focusIntoCard.current = true;
    setLs("open");
  }, [closeLayers, setLs]);

  useEffect(() => () => window.clearTimeout(layersTimer.current), []);

  // Opening moves focus to the first switch, so Escape and Tab work from there.
  useEffect(() => {
    if (ls !== "open" || !focusIntoCard.current) return undefined;
    focusIntoCard.current = false;
    const r = requestAnimationFrame(() => {
      document.getElementById(layersId)?.querySelector<HTMLElement>('[role="switch"]:not(:disabled)')?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(r);
  }, [ls, layersId]);

  // An outside press puts the card away (focus stays where the press went).
  useEffect(() => {
    if (ls !== "open") return undefined;
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node | null;
      if (!t || document.getElementById(layersId)?.contains(t) || layersButton()?.contains(t)) return;
      closeLayers(false);
    };
    document.addEventListener("pointerdown", onDown, true);
    return () => document.removeEventListener("pointerdown", onDown, true);
  }, [ls, layersId, layersButton, closeLayers]);

  // The card closes when its button moves (layout change), when the mobile sheet
  // covers the map, and when the kiosk resets for the next visitor.
  const closeRef = useRef(closeLayers);
  useEffect(() => {
    closeRef.current = closeLayers;
  });
  useEffect(() => {
    closeRef.current(false);
  }, [tier, geo.hideBottom]);
  useEffect(() => {
    const a = on("idle:attract", () => closeRef.current(false));
    const b = on("idle:reset", () => closeRef.current(false));
    return () => {
      a();
      b();
    };
  }, []);

  // Escape while focus is still on the Layers button (§H.1: Escape closes the topmost layer).
  const onControlsKey = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "Escape" || lsRef.current !== "open") return;
    e.preventDefault();
    e.stopPropagation();
    closeLayers(true);
  };

  // §H.2: Layers is a disclosure (aria-expanded). The ZoomIsland primitive marks it
  // aria-pressed; swap the attribute after every commit (React only re-sets it on change).
  useLayoutEffect(() => {
    if (mobile) return;
    const btn = layersButton();
    if (!btn) return;
    btn.removeAttribute("aria-pressed");
    btn.setAttribute("aria-expanded", String(ls === "open"));
    btn.setAttribute("aria-haspopup", "dialog");
  });

  const aboutFromLayers = () => {
    const opener = layersButton();
    closeLayers(false);
    openAbout(opener);
  };

  const rows = useMemo(() => layerRows(LAYER_ROWS, demReady), [demReady]);

  /* ── zoom: keep focus when a button reaches its limit ────────────── */

  const lastPress = useRef<{ dir: 1 | -1; t: number } | null>(null);
  const zoomBy = zoom.zoomBy;
  const zoomIn = useCallback(() => {
    lastPress.current = { dir: 1, t: performance.now() };
    zoomBy(1);
  }, [zoomBy]);
  const zoomOut = useCallback(() => {
    lastPress.current = { dir: -1, t: performance.now() };
    zoomBy(-1);
  }, [zoomBy]);

  useLayoutEffect(() => {
    const p = lastPress.current;
    if (mobile || !p || performance.now() - p.t > ZOOM_FOCUS_MS) return;
    const [btnIn, btnOut] = Array.from(controlsRef.current?.querySelectorAll<HTMLButtonElement>("button") ?? []);
    const active = document.activeElement;
    const lost = !active || active === document.body;
    if (zoom.atMax && p.dir === 1 && (active === btnIn || lost)) btnOut?.focus({ preventScroll: true });
    else if (zoom.atMin && p.dir === -1 && (active === btnOut || lost)) btnIn?.focus({ preventScroll: true });
  }, [zoom.atMax, zoom.atMin, mobile]);

  // §H.2: the layers card is the dialog "Layers" (the design primitive names it "Map layers").
  useLayoutEffect(() => {
    if (ls === "closed") return;
    const card = document.getElementById(layersId);
    if (card && card.getAttribute("aria-label") !== HUD_COPY.layersTitle) card.setAttribute("aria-label", HUD_COPY.layersTitle);
  });

  /* ── the attribution's slot after the map (§H.1) ─────────────────── */

  const rootRef = useRef<HTMLDivElement>(null);
  const [endSlot, setEndSlot] = useState<HTMLElement | null>(null);
  // The layout renders the slot in the same commit as the HUD, and it outlives layout switches.
  useLayoutEffect(() => {
    setEndSlot(rootRef.current?.closest(".ld-loc-layout")?.querySelector<HTMLElement>(END_SLOT) ?? null);
  }, []);

  const onLogo = (e: ReactMouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    onRecenter();
  };

  /* ── render ──────────────────────────────────────────────────────── */

  const idx = (i: number): CSSVars => ({ "--i": stagger ? i : 0 });
  const rootData = {
    "data-tier": tier,
    "data-arrival": arrival,
    "data-drive": drive || undefined,
    "data-reduced": reduced || undefined,
    "data-sheet": mobile ? sheetDir : undefined,
  };
  const zoomStyle = (s: number): CSSProperties | undefined => (s !== 1 ? { zoom: s } : undefined);
  const mobileBottom = geo.mapButtons?.bottom ?? 0;

  const layersCard =
    ls !== "closed" ? (
      <LayersCard
        id={layersId}
        layers={layers}
        rows={rows}
        title={HUD_COPY.layersTitle}
        onLayer={(k, v) => actions.toggleLayer(k, v)}
        onClose={() => closeLayers(true)}
        enter={ls === "open"}
        className={cx("ld-loc-hud-layers", ls === "closing" && "ld-out")}
        style={{
          right: geo.layers.right,
          bottom: geo.layers.bottom,
          ...zoomStyle(geo.layers.scale),
          ...(mobile ? { maxHeight: `calc(100dvh - ${mobileBottom + 28}px - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))` } : null),
        }}
        footer={
          <div className="ld-loc-hud-layers-foot">
            <span>{HUD_COPY.layersFooter}</span>
            <button type="button" className="ld-loc-hud-layers-about" aria-haspopup="dialog" onClick={aboutFromLayers}>
              {HUD_COPY.about}
              <Icon name="chevR" size={13} />
            </button>
          </div>
        }
      />
    ) : null;

  const attribution = (
    <div
      className={cx("ld-loc-hud-slot ld-loc-hud-attrslot", mobile && "ld-loc-hud-follow")}
      style={edgeStyle(geo.attr)}
      data-away={geo.hideBottom || undefined}
      inert={geo.hideBottom}
    >
      <div className="ld-ui-in ld-loc-hud-i ld-loc-hud-dim" style={idx(4)} inert={drive}>
        <Attribution compact={geo.attr.compact} expandable={geo.attr.expandable} powered={geo.attr.powered} kiosk={kiosk} />
      </div>
    </div>
  );

  return (
    <>
      {mobile && (
        // §A.4: the brand on mobile, the Whorl alone at top-left; no container. It runs home() as the panel's logo does.
        <div className="ld-loc-hud ld-loc-hud--brand" role="banner" {...rootData} inert={arrival === "hidden"}>
          <div className="ld-loc-hud-slot ld-loc-hud-logo" style={edgeStyle({ left: MOBILE_LOGO.left, top: MOBILE_LOGO.top })}>
            <div className="ld-ui-in ld-loc-hud-i ld-loc-hud-dim" style={idx(0)} inert={drive}>
              <Logo size={MOBILE_LOGO.size} compact href="#" onClick={onLogo} />
            </div>
          </div>
        </div>
      )}
      <div ref={rootRef} className="ld-loc-hud" role="region" aria-label={HUD_COPY.mapControls} {...rootData} inert={arrival === "hidden"}>
        {/* The time-of-day control (LightIsland) is hidden by client direction: day is the only mood shown.
            Moods still work internally (intro sunrise, ?mood= deep links). */}

        {geo.zoom && (
          <div ref={controlsRef} className="ld-loc-hud-slot ld-loc-hud-zoom" style={edgeStyle(geo.zoom)} onKeyDown={onControlsKey}>
            <div className="ld-ui-in ld-loc-hud-i" style={idx(2)}>
              <ZoomIsland
                onZoomIn={zoomIn}
                onZoomOut={zoomOut}
                onRecenter={onRecenter}
                onLayers={toggleLayers}
                layersOpen={ls === "open"}
                layersId={layersId}
                recenterLabel={HUD_COPY.home}
                zoomInDisabled={zoom.atMax}
                zoomOutDisabled={zoom.atMin}
                style={zoomStyle(geo.zoom.scale)}
              />
            </div>
            {layersCard}
          </div>
        )}

        {geo.mapButtons && (
          <div
            ref={controlsRef}
            className="ld-loc-hud-slot ld-loc-hud-follow"
            style={edgeStyle(geo.mapButtons)}
            data-away={geo.hideBottom || undefined}
            inert={geo.hideBottom}
            onKeyDown={onControlsKey}
          >
            <div className="ld-ui-in ld-loc-hud-i" style={idx(2)}>
              <MapButtons onRecenter={onRecenter} onLayers={toggleLayers} layersOpen={ls === "open"} layersId={layersId} size={geo.mapButtons.size} gap={geo.mapButtons.gap} />
            </div>
            {layersCard}
          </div>
        )}

        {geo.tools && (
          <div className="ld-loc-hud-slot ld-loc-hud-toolslot" style={edgeStyle(geo.tools)}>
            <div className="ld-ui-in ld-loc-hud-i ld-loc-hud-dim" style={idx(3)} inert={drive}>
              <ToolsIsland />
            </div>
          </div>
        )}

        {!endSlot && attribution}
      </div>
      {endSlot &&
        createPortal(
          <div className="ld-loc-hud ld-loc-hud--end" {...rootData} inert={arrival === "hidden"}>
            {attribution}
          </div>,
          endSlot,
        )}
      <AboutSheet kiosk={kiosk} />
    </>
  );
}
